import React, { useState } from 'react';

/**
 * Searchable member picker shared by the Create Invoice and Bulk Create Invoices modals.
 *
 * `multiple` switches between checkbox multi-select (with a Select All header) and
 * radio single-select. Both variants render identical chrome so the two modals match.
 */
const MemberSelectPanel = ({
  members,
  multiple = false,
  value,
  onChange,
  error,
  label = multiple ? 'Select Members' : 'Select Member'
}) => {
  const [memberSearch, setMemberSearch] = useState('');

  // Filter members by search term (name or member ID)
  const filteredMembers = members.filter(member => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (member.organization_name || '').toLowerCase().includes(q) ||
      (member.member_id || '').toLowerCase().includes(q)
    );
  });

  const selectedIds = multiple ? value : (value === '' || value == null ? [] : [value]);

  const handleToggle = (memberId, isSelected) => {
    if (!multiple) {
      onChange(memberId);
      return;
    }
    onChange(
      isSelected
        ? [...value, memberId]
        : value.filter(id => id !== memberId)
    );
  };

  const handleSelectAll = (isSelected) => {
    onChange(isSelected ? members.map(member => member.id) : []);
  };

  const selectedMember = !multiple
    ? members.find(member => member.id === value)
    : null;

  const hasSelection = multiple ? value.length > 0 : !!selectedMember;

  const handleClearSelection = () => {
    onChange(multiple ? [] : '');
  };

  return (
    <div className="form-group">
      <div className="member-select-label">
        <label>{label}</label>
        <div className="member-select-actions">
          {multiple ? (
            <span className="member-selected-count">
              {value.length} selected
            </span>
          ) : (
            selectedMember && (
              <span className="member-selected-count">
                {selectedMember.organization_name}
              </span>
            )
          )}
          {hasSelection && (
            <button
              type="button"
              className="member-clear-selection"
              onClick={handleClearSelection}
            >
              Clear
            </button>
          )}
        </div>
      </div>
      <div className={`member-selection ${error ? 'has-error' : ''}`}>
        <div className="member-search">
          <span className="member-search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by name or member ID…"
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
          />
          {memberSearch && (
            <button
              type="button"
              className="member-search-clear"
              onClick={() => setMemberSearch('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {multiple && (
          <label className="selection-header">
            <input
              type="checkbox"
              checked={members.length > 0 && value.length === members.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
            <span>Select All ({members.length} members)</span>
          </label>
        )}
        <div className="member-list">
          {filteredMembers.length === 0 ? (
            <div className="member-empty">No members match “{memberSearch}”.</div>
          ) : (
            filteredMembers.map(member => {
              const checked = selectedIds.includes(member.id);
              return (
                <label
                  key={member.id}
                  className={`member-item ${checked ? 'selected' : ''}`}
                >
                  <input
                    type={multiple ? 'checkbox' : 'radio'}
                    name={multiple ? undefined : 'member-select'}
                    checked={checked}
                    onChange={(e) => handleToggle(member.id, e.target.checked)}
                  />
                  <span className="member-item-name">{member.organization_name}</span>
                  <span className="member-item-id">{member.member_id}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
      {error && <div className="member-error">{error}</div>}
    </div>
  );
};

export default MemberSelectPanel;
